/**
 * Usage vs. limits panel (FR-BILLING-01/05).
 *
 * One row per hard limit the service reports, with a bar for capped limits and
 * a plain count for unlimited ones. `aria-valuetext` carries the real numbers
 * so the bar is not a colour-only signal, and any limit already exhausted
 * inlines the shared tier-limit CTA — the same component a 402 raises anywhere
 * else in the app.
 */
import { DeskSection } from '../../components/desk-section';
import { TierLimitCta } from '../../components/tier-limit-cta';
import type { BillingUsageRow } from '../../lib/api-types';

import { formatLimit, limitLabel } from './billing-format';

function usagePercent(row: BillingUsageRow): number {
  if (row.limit === null || row.limit === 0) return 0;
  return Math.min(100, Math.round((row.current / row.limit) * 100));
}

export interface UsagePanelProps {
  usage: readonly BillingUsageRow[];
  /** Effective plan name, for the CTA's "on the X plan" line. */
  currentPlanName: string;
  /** Cheapest plan that raises the limits, or `null` at the top tier. */
  nextPlanName: string | null;
  /** Limit deep-linked from a tier-limit CTA (`?limit=seats`) — gets a flag. */
  highlightLimitKey?: string | null;
  /** Omit to render the CTA without an upgrade button (custom contracts). */
  onUpgrade?: () => void;
}

export function UsagePanel({
  usage,
  currentPlanName,
  nextPlanName,
  highlightLimitKey = null,
  onUpgrade,
}: UsagePanelProps): JSX.Element {
  return (
    <DeskSection title="Seats & usage" className="billing-card" data-testid="billing-usage">
      {usage.length === 0 ? (
        <p className="muted">No metered limits apply to this plan.</p>
      ) : (
        <ul className="usage-list">
          {usage.map((row) => {
            const percent = usagePercent(row);
            const label = limitLabel(row.limit_key);
            const isFlagged = highlightLimitKey === row.limit_key;
            return (
              <li
                key={row.limit_key}
                className={`usage-row${isFlagged ? ' usage-row--flagged' : ''}`}
              >
                <div className="usage-row__head">
                  <span className="usage-row__label">{label}</span>
                  <span className="usage-row__value">
                    {formatLimit(row.limit_key, row.current)}
                    {row.is_unlimited ? ' used' : ` / ${formatLimit(row.limit_key, row.limit)}`}
                  </span>
                </div>
                <div
                  className="usage-bar"
                  role="progressbar"
                  aria-label={`${label} usage`}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={row.is_unlimited ? 0 : percent}
                  aria-valuetext={
                    row.is_unlimited
                      ? `${formatLimit(row.limit_key, row.current)} used, unlimited`
                      : `${percent}% of ${formatLimit(row.limit_key, row.limit)}`
                  }
                >
                  <span
                    className={`usage-bar__fill${row.is_at_limit ? ' usage-bar__fill--full' : ''}`}
                    style={{ width: `${row.is_unlimited ? 0 : percent}%` }}
                  />
                </div>
                {row.is_at_limit ? (
                  <TierLimitCta
                    limit={row.limit_key}
                    current={row.current}
                    max={row.limit}
                    variant="inline"
                    currentPlan={currentPlanName}
                    minimumPlan={nextPlanName}
                    onUpgrade={nextPlanName === null ? undefined : onUpgrade}
                  />
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </DeskSection>
  );
}
